import { create } from 'zustand'
import { api } from '../lib/api'
import { useAuthStore } from '../shared/stores/auth'
import type {
  EffectivePermission,
  EnforceMode,
  GetMyPermissionsResponse,
  PermissionAction,
} from '@rochadenis/axiomgo-contracts'

/**
 * STO-242 — a tela esconde o que o perfil não permite, mas só a partir do
 * momento em que o motor diz que a regra vale de verdade.
 *
 * `GET /api/v1/permissions/me` é a única fonte: `{ role, enforceMode,
 * permissions[] }`. Regra do produto (docs/2026-09-12-rbac-o-que-muda-para-o-
 * usuario.md, item 6): enquanto `enforceMode === 'off'` — o padrão de
 * fábrica — preserva a apresentação, exceto escrita para Viewer (QA105). `can()` só passa a filtrar de verdade em
 * `shadow`/`enforce`, e mesmo assim é rede de UX: o motor é quem barra por
 * de verdade (403), a mensagem legível é só para não expor erro cru.
 *
 * `status !== 'loaded'` (ainda não buscou, buscando, ou falhou) também
 * conta como "não esconder nada" — de propósito. O oposto (falha fechada,
 * escondendo por incerteza) é o defeito que esta issue existe para matar:
 * um botão que o perfil PERMITE sumindo da tela por um soluço de rede.
 * QA105: sessão identificada exige papel canônico atual antes de escrita;
 * o papel legado do auth/status não concede autoridade durante essa espera.
 */
type PermissionSubject = { tenantId: string; userId: string }
let generation = 0
let subjectEpoch = 0
const subjectNow = (): PermissionSubject => {
  const { tenantId, userId } = useAuthStore.getState()
  return { tenantId, userId }
}
const sameSubject = (a: PermissionSubject | null, b: PermissionSubject) =>
  a?.tenantId === b.tenantId && a?.userId === b.userId

/** Captured before an async credential lookup; an A→B→A transition stays obsolete. */
export function capturePermissionSubject(tenantId: string, userId: string): () => boolean {
  const epoch = subjectEpoch
  const subject = subjectNow()
  return () =>
    epoch === subjectEpoch &&
    subject.tenantId === tenantId &&
    subject.userId === userId &&
    sameSubject(subject, subjectNow())
}

type PermissionsStatus = 'idle' | 'loading' | 'loaded' | 'unavailable'

interface PermissionsState {
  subject: PermissionSubject | null
  isReadOnly: () => boolean
  status: PermissionsStatus
  enforceMode: EnforceMode
  roleName: string | null
  permissions: EffectivePermission[]
  fetchPermissions: () => Promise<void>
  reset: () => void
  /** `true` quando o perfil TEM o poder, ou quando a regra ainda não se aplica. */
  can: (resource: string, action: PermissionAction) => boolean
}

const IDLE_SLICE = {
  subject: null as PermissionSubject | null,
  status: 'idle' as const,
  enforceMode: 'off' as const,
  roleName: null,
  permissions: [] as EffectivePermission[],
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  ...IDLE_SLICE,

  fetchPermissions: async () => {
    const subject = subjectNow()
    const request = ++generation
    set({ ...IDLE_SLICE, status: 'loading', subject })
    try {
      const res = await api.get<GetMyPermissionsResponse>('/api/v1/permissions/me')
      if (request !== generation || !sameSubject(subject, subjectNow())) return
      set({
        subject,
        status: 'loaded',
        enforceMode: res.enforceMode,
        roleName: res.role?.name ?? null,
        permissions: res.permissions,
      })
    } catch {
      if (request !== generation || !sameSubject(subject, subjectNow())) return
      // Rede caiu ou o motor recusou — não é veredito de permissão nenhum.
      // Ver o comentário da interface: `unavailable` se comporta como `off`.
      set({ status: 'unavailable', enforceMode: 'off', roleName: null, permissions: [] })
    }
  },

  reset: () => {
    generation++
    set({ ...IDLE_SLICE })
  },

  isReadOnly: () => {
    const auth = useAuthStore.getState()
    const { status, roleName, subject } = get()
    const current = status === 'loaded' && sameSubject(subject, subjectNow()) && roleName
    if (current) return current.toLowerCase() === 'viewer'
    return auth.userRole === 'viewer' || Boolean(auth.tenantId || auth.userId)
  },

  can: (resource, action) => {
    if (action !== 'read' && get().isReadOnly()) return false
    const { status, enforceMode, permissions } = get()
    if (status !== 'loaded') return true
    if (enforceMode === 'off') return true
    return permissions.some((p) => p.resource === resource && p.action === action)
  },
}))

// A response for A must never authorize B, including an A→B→A transition.
useAuthStore.subscribe((current, previous) => {
  if (current.tenantId !== previous.tenantId || current.userId !== previous.userId) {
    subjectEpoch++
    usePermissionsStore.getState().reset()
  }
})
